import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BciSegurosFormData } from 'services/types/bciSegurosForm';

import {
  backButtonSx,
  NAVY,
  NAVY_SHADOW,
  NAVY_SHADOW_STRONG,
  SURFACE_LOW,
  secondaryButtonSx,
  YELLOW,
} from '../styleConstants';

const CONFETTI_DOTS = [
  { color: YELLOW, top: '-5px', left: '30%' },
  { color: '#E7233B', top: '10px', left: '90%' },
  { color: '#53B721', top: '70px', left: '15%' },
  { color: NAVY, top: '60px', left: '85%' },
  { color: '#2C5889', top: '-8px', left: '65%' },
  { color: '#25616F', top: '75px', left: '50%' },
  { color: YELLOW, top: '35px', left: '5%' },
  { color: '#53B721', top: '20px', left: '95%' },
];

interface ProposalAndPaymentStepProps {
  formData: BciSegurosFormData;
  onBack: () => void;
}

const ProposalAndPaymentStep: FC<ProposalAndPaymentStepProps> = ({ formData, onBack }) => {
  const navigate = useNavigate();

  const handleDownloadPdf = () => {
    if (!formData.policy.proposalPdf) return;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${formData.policy.proposalPdf}`;
    link.download = `propuesta_${formData.quoteResult.quoteNumber}.pdf`;
    link.click();
  };

  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      {/* Success Icon */}
      <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#53B721',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            boxShadow: '0 4px 20px rgba(83,183,33,0.35)',
            animation: 'scaleIn 0.4s ease-out',
            '@keyframes scaleIn': {
              '0%': { transform: 'scale(0)', opacity: 0 },
              '60%': { transform: 'scale(1.15)' },
              '100%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 44, color: '#fff' }} />
        </Box>
        {/* Confetti dots */}
        {CONFETTI_DOTS.map((dot, i) => (
          <Box
            key={`${dot.color}-${i}`}
            sx={{
              position: 'absolute',
              width: i < 4 ? 8 : 6,
              height: i < 4 ? 8 : 6,
              borderRadius: '50%',
              bgcolor: dot.color,
              top: dot.top,
              left: dot.left,
              animation: `confettiFade 0.6s ease-out ${0.2 + i * 0.08}s both`,
              '@keyframes confettiFade': {
                '0%': { transform: 'scale(0)', opacity: 0 },
                '100%': { transform: 'scale(1)', opacity: 0.7 },
              },
            }}
          />
        ))}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY, mb: 1 }}>
        Propuesta enviada exitosamente
      </Typography>
      <Typography variant="body1" sx={{ color: NAVY, opacity: 0.6, mb: 1, maxWidth: 480, mx: 'auto' }}>
        La propuesta ha sido enviada al correo del cliente.
      </Typography>
      <Typography variant="body2" sx={{ color: NAVY, opacity: 0.6, mb: 4 }}>
        El proceso de cotización ha finalizado.
      </Typography>
      {formData.policy.proposalPdf && (
        <Box
          onClick={handleDownloadPdf}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 1.5,
            borderRadius: '1.5rem',
            bgcolor: SURFACE_LOW,
            boxShadow: NAVY_SHADOW,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'rgba(249, 208, 10, 0.06)',
              transform: 'translateY(-2px)',
              boxShadow: NAVY_SHADOW_STRONG,
            },
            mb: 4,
          }}
        >
          <DownloadIcon sx={{ color: YELLOW }} />
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
              Descargar PDF Propuesta
            </Typography>
            <Typography variant="caption" sx={{ color: NAVY, opacity: 0.6 }}>
              propuesta_{formData.quoteResult.quoteNumber}.pdf
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
        <Button variant="outlined" onClick={onBack} startIcon={<NavigateBeforeIcon />} sx={backButtonSx}>
          Atrás
        </Button>
        <Button variant="contained" onClick={() => navigate('/broker-dashboard')} sx={secondaryButtonSx}>
          Ver pólizas
        </Button>
      </Box>
    </Box>
  );
};

export default ProposalAndPaymentStep;
